import { useEffect, useId, useRef, useState } from 'react';

export type ActionMenuItem = {
  id: string;
  label: string;
  onSelect: () => void;
  disabled?: boolean;
};

type ActionMenuButtonProps = {
  label: string;
  items: ActionMenuItem[];
  variant?: 'primary' | 'secondary';
  disabled?: boolean;
  className?: string;
  badge?: number;
};

export function ActionMenuButton({
  label,
  items,
  variant = 'secondary',
  disabled,
  className,
  badge,
}: ActionMenuButtonProps) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const menuId = useId();

  useEffect(() => {
    if (!open) return undefined;
    const onPointerDown = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  const btnClass = variant === 'primary' ? 'btn-primary btn-sm' : 'btn-secondary btn-sm';

  return (
    <div
      ref={rootRef}
      className={['dem-action-menu', open ? 'dem-action-menu--open' : '', className]
        .filter(Boolean)
        .join(' ')}
    >
      <button
        type="button"
        className={`${btnClass} dem-action-menu__trigger`}
        disabled={disabled}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={menuId}
        onClick={() => setOpen((value) => !value)}
      >
        <span>{label}</span>
        {badge != null && badge > 0 ? (
          <span className="dem-po-orders-email-queue-badge" aria-label={`${badge} pending`}>
            {badge > 99 ? '99+' : badge}
          </span>
        ) : null}
        <span className="dem-action-menu__caret" aria-hidden>
          ▾
        </span>
      </button>
      {open ? (
        <div id={menuId} className="dem-action-menu__panel" role="menu">
          {items.map((item) => (
            <button
              key={item.id}
              type="button"
              role="menuitem"
              className="dem-action-menu__item"
              disabled={item.disabled || disabled}
              onClick={() => {
                setOpen(false);
                item.onSelect();
              }}
            >
              {item.label}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}
